import { useCallback, useEffect, useState } from 'react'

import type { AcademySearchApiPayload } from '@/lib/academySearch/academySearch.interface'
import { buildEmptyPayload } from '@/lib/academySearch/utils/buildEmptyPayload'

import {
  buildSearchUrl,
  fetchSearch,
  getSearchErrorMessage,
  isAbortError,
  isSearchQueryTooShort,
} from '../search-modal.utils'

export function useSearchPayload(
  open: boolean,
  debouncedQuery: string
): {
  clearFetchError: () => void
  fetchError: string | null
  isLoading: boolean
  payload: AcademySearchApiPayload
} {
  const [payload, setPayload] = useState(buildEmptyPayload)
  const [isLoading, setIsLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      return undefined
    }

    if (isSearchQueryTooShort(debouncedQuery)) {
      setPayload(buildEmptyPayload())
      setFetchError(null)
      setIsLoading(false)
      return undefined
    }

    const ctrl = new AbortController()
    setIsLoading(true)
    setFetchError(null)

    const url = buildSearchUrl(debouncedQuery)

    const run = async () => {
      try {
        const data = await fetchSearch(url, { signal: ctrl.signal }, debouncedQuery)
        setPayload(data)
      } catch (err: unknown) {
        if (isAbortError(err)) {
          return
        }
        setFetchError(getSearchErrorMessage(err))
        setPayload(buildEmptyPayload())
      } finally {
        setIsLoading(false)
      }
    }

    void run()

    return () => ctrl.abort()
  }, [open, debouncedQuery])

  const clearFetchError = useCallback(() => {
    setFetchError(null)
  }, [])

  return { clearFetchError, fetchError, isLoading, payload }
}
